/* eslint-disable import/no-cycle */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/index';
import { NewOrderItem } from 'types';
import { getShipmentInfo } from 'pages/Shipping/utils';

export interface OrderItemChange {
  id: string;
  orderId: string;
  serialNumber?: string | null;
  originalSerialNumber?: string | null;
  trackingNumber?: string | null;
  originalTrackingNumber?: string | null;
  carrierId?: string | null;
  originalCarrierId?: string | null;
}

export interface OrderItemChangesState {
  changes: Record<string, OrderItemChange>;
  originalOrderItems: Record<string, NewOrderItem>;
}

const initialState: OrderItemChangesState = {
  changes: {},
  originalOrderItems: {},
};

export const orderItemChangesSlice = createSlice({
  name: 'orderItemChangesSlice',
  initialState,
  reducers: {
    updateOrderItemSerialNumber: (
      state,
      action: PayloadAction<{
        id: string;
        orderId: string;
        serialNumber: string;
        originalOrderItem?: NewOrderItem;
      }>
    ) => {
      const { id, orderId, serialNumber, originalOrderItem } = action.payload;

      // Store the original order item if it's not already stored
      if (originalOrderItem && !state.originalOrderItems[id]) {
        state.originalOrderItems[id] = originalOrderItem;
      }

      // Store the change
      state.changes[id] = {
        ...(state.changes[id] || { id, orderId }),
        serialNumber,
        originalSerialNumber:
          state.changes[id]?.originalSerialNumber ||
          state.originalOrderItems[id]?.serialNumber ||
          originalOrderItem?.serialNumber,
      };
    },
    updateOrderItemTrackingNumber: (
      state,
      action: PayloadAction<{
        id: string;
        orderId: string;
        trackingNumber: string | null;
        originalOrderItem?: NewOrderItem;
      }>
    ) => {
      const { id, orderId, trackingNumber, originalOrderItem } = action.payload;
      
      // Store the original order item if it's not already stored
      if (originalOrderItem && !state.originalOrderItems[id]) {
        state.originalOrderItems[id] = originalOrderItem;
      }

      // Get the original tracking number
      const originalTrackingNumber = state.changes[id]?.originalTrackingNumber ||
        (state.originalOrderItems[id]
          ? getShipmentInfo(state.originalOrderItems[id]).trackingNumber
          : originalOrderItem
            ? getShipmentInfo(originalOrderItem).trackingNumber
            : null);

      // If the new value matches the original, remove the tracking number changes
      if (trackingNumber === originalTrackingNumber) {
        if (state.changes[id]) {
          const { trackingNumber: _, originalTrackingNumber: __, ...rest } = state.changes[id];
          if (Object.keys(rest).length > 2) { // if there are other changes besides id and orderId
            state.changes[id] = rest; // Keep other changes
          } else {
            delete state.changes[id]; // Remove the entire entry if no other changes
          }
        }
        return;
      }

      // Store the change
      state.changes[id] = {
        ...(state.changes[id] || { id, orderId }),
        trackingNumber,
        originalTrackingNumber,
      };
    },
    updateOrderItemCarrier: (
      state,
      action: PayloadAction<{
        id: string;
        orderId: string;
        carrierId: string;
        originalOrderItem?: NewOrderItem;
      }>
    ) => {
      const { id, orderId, carrierId, originalOrderItem } = action.payload;

      // Store the original order item if it's not already stored
      if (originalOrderItem && !state.originalOrderItems[id]) {
        state.originalOrderItems[id] = originalOrderItem;
      }

      // Store the change
      state.changes[id] = {
        ...(state.changes[id] || { id, orderId }),
        carrierId,
        originalCarrierId:
          state.changes[id]?.originalCarrierId ||
          (state.originalOrderItems[id]
            ? getShipmentInfo(state.originalOrderItems[id]).shipmentCarrierId
            : originalOrderItem
              ? getShipmentInfo(originalOrderItem).shipmentCarrierId
              : null),
      };
    },
    clearOrderItemChanges: (state) => {
      state.changes = {};
      // Also clear the original order items to ensure fresh data on next load
      state.originalOrderItems = {};
    },
    clearOrderItemChange: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      delete state.changes[id];
    },
    storeOriginalOrderItems: (state, action: PayloadAction<NewOrderItem[]>) => {
      const orderItems = action.payload;
      orderItems.forEach((orderItem) => {
        if (orderItem.id) {
          // Always update the original order item, even if it exists
          // This ensures we have the latest data after changes
          state.originalOrderItems[orderItem.id] = orderItem;
        }
      });
    },
    clearOriginalOrderItems: (state) => {
      state.originalOrderItems = {};
    },
    revertToOriginal: (state) => {
      state.changes = {};
    },
  },
});

export const {
  updateOrderItemSerialNumber,
  updateOrderItemTrackingNumber,
  updateOrderItemCarrier,
  clearOrderItemChanges,
  clearOrderItemChange,
  storeOriginalOrderItems,
  clearOriginalOrderItems,
  revertToOriginal,
} = orderItemChangesSlice.actions;

// Selectors
export const selectOrderItemChanges = (state: RootState) =>
  state.shipping.orderItemChanges.changes;

export const selectOrderItemChange = (id: string) => (state: RootState) =>
  state.shipping.orderItemChanges.changes[id];

export const selectOriginalOrderItems = (state: RootState) =>
  state.shipping.orderItemChanges.originalOrderItems;

export const selectOriginalOrderItem = (id: string) => (state: RootState) =>
  state.shipping.orderItemChanges.originalOrderItems[id];

export default orderItemChangesSlice.reducer;
